import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { useOutsideClick } from '../../../js/Helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

export default function LabelColorPicker({ colorClass, options = [], disabled = false, onChange }) {
	const ref = useRef();
	const dropdown_ref = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [dropdownStyle, setDropdownStyle] = useState({});

	useOutsideClick(ref, () => {
		setIsOpen(false);
	});

	useEffect(() => {
		if(disabled) setIsOpen(false);
	}, [disabled]);

	useEffect(() => {
		if(!isOpen || typeof window === 'undefined') return;

		const positionDropdown = () => {
			if(!ref.current) return;

			const trigger_rect = ref.current.getBoundingClientRect();
			const dropdown_width = dropdown_ref.current?.offsetWidth || 220;
			const dropdown_height = dropdown_ref.current?.offsetHeight || 150;
			const margin = 10;

			let left = trigger_rect.left;
			left = Math.min(Math.max(margin, left), (window.innerWidth || 1200) - dropdown_width - margin);

			let top = trigger_rect.bottom + 6;
			if(top + dropdown_height > (window.innerHeight || 800) - margin)
			{
				top = trigger_rect.top - dropdown_height - 6;
			}
			top = Math.max(margin, top);

			setDropdownStyle({
				left,
				top
			});
		};

		positionDropdown();
		const frame_id = window.requestAnimationFrame(positionDropdown);
		window.addEventListener('resize', positionDropdown);
		window.addEventListener('scroll', positionDropdown, true);

		return () => {
			window.cancelAnimationFrame(frame_id);
			window.removeEventListener('resize', positionDropdown);
			window.removeEventListener('scroll', positionDropdown, true);
		};
	}, [isOpen, options.length]);

	const selected_option = options.find((option) => option.value === colorClass);
	const selected_color = selected_option ? selected_option.value : colorClass;
	const dropdown_markup = (
		<div className='create-labels-color-dropdown create-labels-color-dropdown-floating click-restricted' ref={dropdown_ref} style={dropdownStyle}>
			{options.map((option) => (
				<button
					type='button'
					key={option.value}
					className={`create-labels-color-option click-restricted ${option.value === selected_color ? 'selected' : ''} ${option.disabled ? 'inactive' : ''}`}
					onClick={() => {
						if(option.disabled) return;
						setIsOpen(false);
						if(onChange) onChange(option.value);
					}}
					aria-label={option.display}
					title={option.disabled ? `${option.display} (In use)` : option.display}
				>
					<div className={`create-labels-color-dot click-restricted ${option.value}`}></div>
				</button>
			))}
		</div>
	);

	return (
		<div className={`create-labels-color-picker ${isOpen ? 'open' : ''}`} ref={ref}>
			<button
				type='button'
				className={`create-labels-color-trigger ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
				onClick={() => !disabled ? setIsOpen(!isOpen) : null}
				aria-label={selected_option ? selected_option.display : 'Label color'}
				title={selected_option ? selected_option.display : 'Label color'}
			>
				<div className={`create-labels-color-dot click-restricted ${selected_color}`}></div>
				<FontAwesomeIcon icon={faChevronDown} className='create-labels-color-chevron click-restricted' />
			</button>
			{(!disabled && isOpen) && (
				(typeof document !== 'undefined') ? createPortal(dropdown_markup, document.body) : dropdown_markup
			)}
		</div>
	);
}
